import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { NEUTRAL, STATUS_COLORS } from './designTokens';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';

const od = {
  pageBg:      NEUTRAL.page,
  headerBg:    NEUTRAL.ink,
  accent:      KLINIO_DEFAULT_ACCENT,
  textPrimary: NEUTRAL.ink,
  textMuted:   NEUTRAL.muted,
  cardBg:      NEUTRAL.card,
  border:      NEUTRAL.line,
  success:     STATUS_COLORS.success,
  warning:     STATUS_COLORS.warning,
  info:        STATUS_COLORS.info,
  neutral:     NEUTRAL.muted,
};

function getOnboardingStatus(t) {
  const clients      = Number(t.client_count);
  const isReadyForTest = t.has_display_name && t.has_active_service && t.has_schedule;
  const hasAnything  = t.has_brand || t.has_active_service || t.has_schedule || clients > 0;

  if (!hasAnything)
    return { label: 'Ny tenant',            color: od.neutral };
  if (!isReadyForTest)
    return { label: 'Grundsetup saknas',    color: od.warning };
  if (clients === 0)
    return { label: 'Redo för klienttest',  color: od.info };
  return   { label: 'Aktiv tenant',         color: od.success };
}

function clientLink(therapistId) {
  return `${window.location.origin}/?t=${therapistId}`;
}

export default function OwnerDashboard({ session, isTherapist, onNavigateBack }) {
  const [therapists, setTherapists] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [copied,     setCopied]     = useState(null);

  useEffect(() => {
    fetchOverview();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOverview = async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('get_therapist_overview');
    if (rpcError) setError(rpcError.message);
    else setTherapists(data || []);
    setLoading(false);
  };

  const handleCopy = (therapistId) => {
    navigator.clipboard.writeText(clientLink(therapistId)).then(() => {
      setCopied(therapistId);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: od.pageBg }}>

      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <div style={{
        background: od.headerBg,
        padding: '13px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {isTherapist && (
            <button
              onClick={onNavigateBack}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: NEUTRAL.faint,
                cursor: 'pointer',
                padding: '5px 12px',
                borderRadius: '7px',
                fontSize: '0.82em',
                transition: 'background 0.1s',
              }}
            >
              ← Min klinik
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{ width: '7px', height: '7px', background: od.accent, borderRadius: '2px' }} />
            <span style={{ color: NEUTRAL.line, fontWeight: '700', fontSize: '0.875em', letterSpacing: '-0.1px' }}>
              Klinio Admin
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: NEUTRAL.text, fontSize: '0.78em' }}>{session?.user?.email}</span>
          <button
            onClick={() => supabase.auth.signOut()}
            onMouseEnter={e => { e.currentTarget.style.color = NEUTRAL.faint; }}
            onMouseLeave={e => { e.currentTarget.style.color = NEUTRAL.text; }}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.1)',
              color: NEUTRAL.text,
              cursor: 'pointer',
              padding: '5px 10px',
              borderRadius: '6px',
              fontSize: '0.78em',
              transition: 'color 0.1s',
            }}
          >
            Logga ut
          </button>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>

        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 5px', fontSize: '1.35em', fontWeight: '700', color: od.textPrimary, letterSpacing: '-0.4px' }}>
            Terapeutöversikt
          </h1>
          <p style={{ margin: 0, color: od.textMuted, fontSize: '0.875em' }}>
            Läsläge · {loading ? '…' : `${therapists.length} terapeut${therapists.length !== 1 ? 'er' : ''}`}
          </p>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: od.textMuted, fontSize: '0.875em' }}>
            Laddar…
          </div>
        )}

        {error && !loading && (
          <div style={{
            background: STATUS_COLORS.error_bg, border: `1px solid ${STATUS_COLORS.error}40`,
            borderRadius: '10px', padding: '16px 20px',
            color: STATUS_COLORS.error, fontSize: '0.875em',
          }}>
            Fel vid hämtning: {error}
          </div>
        )}

        {!loading && !error && therapists.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '60px 0',
            color: od.textMuted, background: od.cardBg,
            borderRadius: '12px', border: `1px solid ${od.border}`,
            fontSize: '0.875em',
          }}>
            Inga terapeuter hittades.
          </div>
        )}

        {!loading && !error && therapists.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {therapists.map((t) => {
              const status   = getOnboardingStatus(t);
              const isCopied = copied === t.therapist_id;
              const clients  = Number(t.client_count);
              const services = Number(t.services_count);

              return (
                <div
                  key={t.therapist_id}
                  style={{
                    background: od.cardBg,
                    border: `1px solid ${od.border}`,
                    borderRadius: '12px',
                    padding: '18px 20px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>

                    {/* Left: info */}
                    <div style={{ flex: 1, minWidth: 0 }}>

                      {/* Name row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '700', color: od.textPrimary, fontSize: '0.97em' }}>
                          {t.business_name || t.therapist_name || '–'}
                        </span>
                        {t.business_name && t.therapist_name && (
                          <span style={{ color: od.textMuted, fontSize: '0.8em' }}>({t.therapist_name})</span>
                        )}
                        <span style={{
                          background: `${status.color}1a`,
                          color: status.color,
                          borderRadius: '8px',
                          padding: '2px 8px',
                          fontSize: '0.73em',
                          fontWeight: '600',
                          letterSpacing: '0.01em',
                        }}>
                          {status.label}
                        </span>
                      </div>

                      {/* Email + date */}
                      <div style={{ color: od.textMuted, fontSize: '0.81em', marginBottom: '12px' }}>
                        {t.therapist_email || '–'}
                        {t.created_at && (
                          <span style={{ marginLeft: '10px', color: NEUTRAL.faint }}>
                            Skapad {new Date(t.created_at).toLocaleDateString('sv-SE')}
                          </span>
                        )}
                      </div>

                      {/* Stats row */}
                      <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '0.8em' }}>
                        <span style={{ color: od.textMuted }}>
                          <span style={{ fontWeight: '600', color: t.has_display_name ? od.success : od.warning }}>
                            {t.has_display_name ? '✓' : '✗'}
                          </span>
                          {' '}visningsnamn
                        </span>
                        <span style={{ color: od.textMuted }}>
                          <span style={{ fontWeight: '600', color: t.has_active_service ? od.success : od.warning }}>
                            {services}
                          </span>
                          {' '}tjänster{t.has_active_service ? '' : ' (inga aktiva)'}
                        </span>
                        <span style={{ color: od.textMuted }}>
                          <span style={{ fontWeight: '600', color: t.has_schedule ? od.success : od.warning }}>
                            {t.has_schedule ? '✓' : '✗'}
                          </span>
                          {' '}schema
                        </span>
                        <span style={{ color: od.textMuted }}>
                          <span style={{ fontWeight: '600', color: od.textPrimary }}>{clients}</span>
                          {' '}kunder
                        </span>
                        {t.has_reply_email && (
                          <span style={{ color: od.textMuted }}>
                            <span style={{ fontWeight: '600', color: od.success }}>✓</span>
                            {' '}svarsmejl
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: client link */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.73em', color: NEUTRAL.faint }}>
                        Klientlänk redo
                      </span>
                      <button
                        onClick={() => handleCopy(t.therapist_id)}
                        onMouseEnter={e => { if (!isCopied) e.currentTarget.style.background = NEUTRAL.line; }}
                        onMouseLeave={e => { if (!isCopied) e.currentTarget.style.background = NEUTRAL.page; }}
                        style={{
                          background: isCopied ? STATUS_COLORS.success_bg : NEUTRAL.page,
                          border: 'none',
                          color:  isCopied ? od.success : od.textMuted,
                          cursor: 'pointer',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          fontSize: '0.78em',
                          fontWeight: '500',
                          transition: 'all 0.15s',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isCopied ? 'Kopierad!' : 'Kopiera klientlänk'}
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
